import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { CheckCircle2, ChevronRight, ChevronLeft, Loader2 } from 'lucide-react';
import { BACKEND_URL } from '../../config/api';
import { todayYMD, addDaysYMD } from '../../utils/dateHelpers';
import { useAuth } from '../../hooks/useAuth';

const WORKER_TYPES = [
  { value: 'individual_tradesperson', label: 'Individual tradesperson' },
  { value: 'self_employed_subcontractor', label: 'Self-employed subcontractor' },
  { value: 'small_trade_team_gang', label: 'Small trade team / gang' },
  { value: 'limited_company_trade_contractor', label: 'Limited-company trade contractor' },
];
const WORK_RADIUS = [5, 10, 15, 25, 50];
const WORK_PREFS = [
  { value: 'day_rate', label: 'Day rate' },
  { value: 'price_work', label: 'Price work' },
  { value: 'both', label: 'Both' },
];

const AVAIL_STATUSES = [
  { value: 'available', label: 'Available', cls: 'bg-emerald-100 text-emerald-800' },
  { value: 'evening_only', label: 'Evening only', cls: 'bg-amber-100 text-amber-800' },
  { value: 'weekend_only', label: 'Weekend only', cls: 'bg-sky-100 text-sky-800' },
  { value: 'booked', label: 'Booked', cls: 'bg-slate-200 text-slate-700' },
  { value: 'unavailable', label: 'Unavailable', cls: 'bg-rose-100 text-rose-700' },
];

const STEPS = [
  { key: 'basics', label: 'Basics' },
  { key: 'trade', label: 'Trade & experience' },
  { key: 'location', label: 'Location & radius' },
  { key: 'rates', label: 'Work preference & rate' },
  { key: 'about', label: 'About you' },
  { key: 'availability', label: 'Availability (14 days)' },
];


function ProgressPill({ percent }) {
  return (
    <div className="flex items-center gap-3">
      <div className="h-2 flex-1 bg-slate-100 rounded-full overflow-hidden">
        <div className="h-full bg-orange-500 transition-all" style={{ width: `${percent || 0}%` }} />
      </div>
      <span className="text-xs text-slate-500 whitespace-nowrap">{percent || 0}%</span>
    </div>
  );
}


export default function TradespersonOnboarding() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [stepIdx, setStepIdx] = useState(0);
  const [saving, setSaving] = useState(false);
  const [state, setState] = useState(null);
  const [form, setForm] = useState({});
  const [dateMap, setDateMap] = useState({});

  const fetchState = useCallback(async () => {
    try {
      const s = await axios.get(`${BACKEND_URL}/api/onboarding/state`, { withCredentials: true });
      setState(s.data);
    } catch (_e) { /* ignore — auth handled elsewhere */ }
  }, []);

  // Hydrate the form from current user + profile so autosave doesn't
  // wipe pre-existing answers when the wizard first opens.
  const hydrateFromServer = useCallback(async () => {
    try {
      const meRes = await axios.get(`${BACKEND_URL}/api/auth/me`, { withCredentials: true });
      const me = meRes.data || {};
      // The tradesperson profile lives at /profile/{user_id} — the
      // parameterless /profile route is POST/PUT for edits only (returns
      // 405 on GET). Fetch the parameterised route so the wizard
      // prefills main_trade, bio, radius, rate, etc. for returning users.
      let prof = {};
      if (me.user_id) {
        try {
          const profRes = await axios.get(`${BACKEND_URL}/api/tradespeople/profile/${me.user_id}`,
            { withCredentials: true });
          prof = profRes.data || {};
        } catch (_p) { prof = {}; }
      }
      setForm({
        name: me.name || '',
        phone: me.phone || '',
        worker_type: me.worker_type || '',
        postcode: me.postcode || prof.postcode || '',
        main_trade: prof.main_trade || prof.trade || '',
        years_experience: prof.years_experience ?? '',
        work_radius_miles: prof.work_radius_miles || null,
        preferred_work_type:
          Array.isArray(prof.preferred_work_type)
            ? prof.preferred_work_type
            : prof.preferred_work_type ? [prof.preferred_work_type] : [],
        daily_rate: prof.daily_rate ?? '',
        bio: prof.bio || '',
        own_transport: prof.own_transport,
        public_liability_insurance: prof.public_liability_insurance,
        // Conditional
        team_name: prof.team_name || '',
        team_lead_name: prof.team_lead_name || '',
        number_of_workers_available: prof.number_of_workers_available ?? '',
        registered_company_name: prof.registered_company_name || '',
        company_number: prof.company_number || '',
      });
    } catch (_e) { /* keep form blank */ }
  }, []);

  useEffect(() => {
    hydrateFromServer();
    fetchState();
  }, [hydrateFromServer, fetchState]);

  // Preload the next 14 days into dateMap with unset status. The trade
  // must actively pick a status for each — spec §Availability. Uses
  // local calendar day (not UTC) to avoid the yesterday/tomorrow shift
  // for anyone west/east of UTC.
  useEffect(() => {
    const today = todayYMD();
    const next = {};
    for (let i = 0; i < 14; i++) {
      const iso = addDaysYMD(today, i);
      next[iso] = null;
    }
    setDateMap(next);
  }, []);

  const activeStep = STEPS[stepIdx];
  const update = (patch) => setForm((f) => ({ ...f, ...patch }));

  const saveStep = async (fields, step) => {
    setSaving(true);
    try {
      const res = await axios.patch(`${BACKEND_URL}/api/onboarding/tradesperson`,
        { step, ...fields }, { withCredentials: true });
      setState(res.data);
      return true;
    } catch (e) {
      const detail = e.response?.data?.detail;
      const msg = typeof detail === 'object' ? detail.message : detail || e.message;
      toast.error(msg || 'Save failed');
      return false;
    } finally {
      setSaving(false);
    }
  };

  const goNext = async () => {
    let payload = {};
    if (activeStep.key === 'basics') {
      if (!form.name || !form.phone || !form.worker_type) {
        toast.error('Please answer every field on this step');
        return;
      }
      payload = { name: form.name, phone: form.phone, worker_type: form.worker_type };
    } else if (activeStep.key === 'trade') {
      if (!form.main_trade || !form.years_experience) {
        toast.error('Please add your main trade and years of experience');
        return;
      }
      payload = { main_trade: form.main_trade, years_experience: parseInt(form.years_experience, 10) };
      // Team/ltd conditional fields
      if (form.worker_type === 'small_trade_team_gang') {
        if (!form.team_name || !form.team_lead_name || !form.number_of_workers_available) {
          toast.error('Please complete every team detail');
          return;
        }
        payload.team_name = form.team_name;
        payload.team_lead_name = form.team_lead_name;
        payload.number_of_workers_available = parseInt(form.number_of_workers_available, 10);
      }
      if (form.worker_type === 'limited_company_trade_contractor') {
        if (!form.registered_company_name || !form.company_number) {
          toast.error('Please complete every company detail');
          return;
        }
        payload.registered_company_name = form.registered_company_name;
        payload.company_number = form.company_number;
      }
    } else if (activeStep.key === 'location') {
      if (!form.postcode || !form.work_radius_miles) {
        toast.error('Please add a postcode and pick a work radius');
        return;
      }
      payload = { postcode: form.postcode, work_radius_miles: parseInt(form.work_radius_miles, 10) };
    } else if (activeStep.key === 'rates') {
      const pwts = form.preferred_work_type || [];
      if (!pwts.length) {
        toast.error('Please pick a work preference');
        return;
      }
      const needsRate = pwts.includes('day_rate') || pwts.includes('both');
      if (needsRate && (!form.daily_rate || Number(form.daily_rate) <= 0)) {
        toast.error('Please add your day rate');
        return;
      }
      payload = { preferred_work_type: pwts };
      if (needsRate) payload.daily_rate = Number(form.daily_rate);
    } else if (activeStep.key === 'about') {
      if (!form.bio || form.bio.length < 20) {
        toast.error('Please add a short profile description (min 20 chars)');
        return;
      }
      if (form.own_transport === undefined || form.own_transport === null) {
        toast.error('Please answer the van/transport question');
        return;
      }
      if (form.public_liability_insurance === undefined || form.public_liability_insurance === null) {
        toast.error('Please answer the insurance question');
        return;
      }
      payload = { bio: form.bio, own_transport: form.own_transport, public_liability_insurance: form.public_liability_insurance };
    }

    const ok = await saveStep(payload, activeStep.key);
    if (ok) setStepIdx((i) => Math.min(i + 1, STEPS.length - 1));
  };

  const submitAvailability = async () => {
    const dates = Object.entries(dateMap)
      .filter(([, v]) => v)
      .map(([date, status]) => ({ date, status }));
    if (dates.length !== 14) {
      toast.error(`Please pick a status for every day (${dates.length}/14 done)`);
      return;
    }
    setSaving(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/onboarding/tradesperson/confirm-availability`,
        { dates }, { withCredentials: true });
      setState(res.data);
      if (res.data.is_searchable) {
        toast.success('Your profile is live! You will now appear in search.');
        navigate('/', { replace: true });
      } else if (res.data.is_complete) {
        toast.success('Profile complete. You will appear in search on the days you confirmed as available.');
        navigate('/', { replace: true });
      } else {
        toast.warning('Almost there — some earlier fields still need answering.');
      }
    } catch (e) {
      const detail = e.response?.data?.detail;
      const msg = typeof detail === 'object' ? detail.message : detail || 'Failed';
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const percent = state?.completion_percent ?? 0;

  const isCurrentStepValid = useMemo(() => {
    // Lightweight enable-check to disable the next button when obviously invalid.
    if (activeStep?.key === 'basics') return !!(form.name && form.phone && form.worker_type);
    if (activeStep?.key === 'trade') return !!(form.main_trade && form.years_experience);
    if (activeStep?.key === 'location') return !!(form.postcode && form.work_radius_miles);
    if (activeStep?.key === 'rates') return !!(form.preferred_work_type?.length);
    if (activeStep?.key === 'about') return !!(form.bio && form.own_transport != null && form.public_liability_insurance != null);
    return true;
  }, [activeStep, form]);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="tradesperson-onboarding">
      <div className="max-w-2xl mx-auto p-4 sm:p-6 md:p-8">
        <div className="mb-6">
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">Complete your profile</h1>
          <p className="text-sm text-slate-500 mt-1">You will appear in employer search once every step is done and your next 14 days are confirmed.</p>
          <div className="mt-4"><ProgressPill percent={percent} /></div>
        </div>

        {/* Supportive legacy notice — surfaces missing fields and a
            one-click continue that resumes at the first missing step. */}
        {state && !state.is_complete && (state.missing_fields || []).length > 0 && (
          <div className="mb-6 rounded-2xl border border-amber-200 bg-amber-50 p-4 sm:p-5" data-testid="legacy-onboarding-notice">
            <p className="text-sm font-semibold text-amber-900">
              Complete your profile to appear in employer searches.
            </p>
            <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 gap-3 text-xs text-amber-900">
              <div><span className="block text-amber-700 uppercase text-[10px]">Complete</span>
                <span className="font-semibold text-base" data-testid="legacy-notice-percent">{percent}%</span></div>
              <div><span className="block text-amber-700 uppercase text-[10px]">Fields left</span>
                <span className="font-semibold text-base" data-testid="legacy-notice-remaining">{state.missing_count}</span></div>
              <div><span className="block text-amber-700 uppercase text-[10px]">Status</span>
                <span className="font-semibold text-sm capitalize">{(state.status || '').replaceAll('_', ' ')}</span></div>
            </div>
            {(state.missing_field_labels || []).length > 0 && (
              <div className="mt-3">
                <p className="text-xs text-amber-800 mb-1.5">Still to do:</p>
                <div className="flex flex-wrap gap-1.5" data-testid="legacy-notice-missing-list">
                  {state.missing_field_labels.slice(0, 12).map((lbl) => (
                    <span key={lbl} className="text-[11px] px-2 py-0.5 rounded-full bg-white border border-amber-200 text-amber-900">{lbl}</span>
                  ))}
                </div>
              </div>
            )}
            <button type="button" onClick={() => {
              // Automatic resume — jump to the first step whose fields are
              // still incomplete based on the canonical missing_fields list.
              const missing = new Set(state.missing_fields || []);
              const stepOrder = [
                { key: 'basics', fields: ['name', 'phone', 'worker_type'] },
                { key: 'trade', fields: ['main_trade', 'years_experience', 'team_name', 'team_lead_name', 'number_of_workers_available', 'registered_company_name', 'company_number'] },
                { key: 'location', fields: ['postcode', 'work_radius_miles'] },
                { key: 'rates', fields: ['preferred_work_type', 'daily_rate'] },
                { key: 'about', fields: ['bio', 'own_transport', 'public_liability_insurance'] },
                { key: 'availability', fields: ['availability_confirmed_14d'] },
              ];
              const target = stepOrder.findIndex((s) => s.fields.some((f) => missing.has(f)));
              if (target >= 0) setStepIdx(target);
            }} className="btn-primary mt-4 w-full sm:w-auto" data-testid="legacy-notice-continue">
              Continue where you left off
            </button>
          </div>
        )}

        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-5 sm:p-6">
          <div className="flex flex-wrap gap-1.5 mb-4">
            {STEPS.map((s, i) => (
              <span key={s.key}
                className={`text-[11px] px-2 py-1 rounded-full font-medium ${i === stepIdx ? 'bg-orange-100 text-orange-700' : i < stepIdx ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}
                data-testid={`ob-step-badge-${s.key}`}>
                {i < stepIdx && <CheckCircle2 className="h-3 w-3 inline mr-1" />}
                {s.label}
              </span>
            ))}
          </div>

          {activeStep.key === 'basics' && (
            <div className="space-y-3" data-testid="step-basics">
              <div><label className="text-sm font-medium">Full name</label>
                <input className="w-full mt-1 h-10 px-3 rounded-lg border border-slate-200" value={form.name || ''}
                  onChange={(e) => update({ name: e.target.value })} data-testid="ob-input-name" /></div>
              <div><label className="text-sm font-medium">Mobile number</label>
                <input className="w-full mt-1 h-10 px-3 rounded-lg border border-slate-200" value={form.phone || ''}
                  onChange={(e) => update({ phone: e.target.value })} data-testid="ob-input-phone" /></div>
              <div><label className="text-sm font-medium">I am a...</label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-1">
                  {WORKER_TYPES.map((wt) => (
                    <button type="button" key={wt.value}
                      onClick={() => update({ worker_type: wt.value })}
                      data-testid={`ob-wt-${wt.value}`}
                      className={`text-left rounded-lg border px-3 py-2 text-sm ${form.worker_type === wt.value ? 'border-orange-500 bg-orange-50' : 'border-slate-200'}`}>
                      {wt.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeStep.key === 'trade' && (
            <div className="space-y-3" data-testid="step-trade">
              <div><label className="text-sm font-medium">Main trade</label>
                <input className="w-full mt-1 h-10 px-3 rounded-lg border border-slate-200" value={form.main_trade || ''}
                  onChange={(e) => update({ main_trade: e.target.value })} placeholder="e.g. Electrician" data-testid="ob-input-main-trade" /></div>
              <div><label className="text-sm font-medium">Years of experience</label>
                <input type="number" min={0} className="w-full mt-1 h-10 px-3 rounded-lg border border-slate-200"
                  value={form.years_experience ?? ''} onChange={(e) => update({ years_experience: e.target.value })} data-testid="ob-input-years" /></div>
              {form.worker_type === 'small_trade_team_gang' && (
                <>
                  <div><label className="text-sm font-medium">Team / gang name</label>
                    <input className="w-full mt-1 h-10 px-3 rounded-lg border border-slate-200" value={form.team_name || ''}
                      onChange={(e) => update({ team_name: e.target.value })} data-testid="ob-input-team-name" /></div>
                  <div><label className="text-sm font-medium">Team lead name</label>
                    <input className="w-full mt-1 h-10 px-3 rounded-lg border border-slate-200" value={form.team_lead_name || ''}
                      onChange={(e) => update({ team_lead_name: e.target.value })} data-testid="ob-input-team-lead" /></div>
                  <div><label className="text-sm font-medium">Team size</label>
                    <input type="number" min={2} className="w-full mt-1 h-10 px-3 rounded-lg border border-slate-200"
                      value={form.number_of_workers_available ?? ''} onChange={(e) => update({ number_of_workers_available: e.target.value })} data-testid="ob-input-team-size" /></div>
                </>
              )}
              {form.worker_type === 'limited_company_trade_contractor' && (
                <>
                  <div><label className="text-sm font-medium">Registered company name</label>
                    <input className="w-full mt-1 h-10 px-3 rounded-lg border border-slate-200" value={form.registered_company_name || ''}
                      onChange={(e) => update({ registered_company_name: e.target.value })} data-testid="ob-input-company-name" /></div>
                  <div><label className="text-sm font-medium">Companies House number</label>
                    <input className="w-full mt-1 h-10 px-3 rounded-lg border border-slate-200" value={form.company_number || ''}
                      onChange={(e) => update({ company_number: e.target.value })} data-testid="ob-input-company-number" /></div>
                </>
              )}
            </div>
          )}

          {activeStep.key === 'location' && (
            <div className="space-y-3" data-testid="step-location">
              <div><label className="text-sm font-medium">Base postcode</label>
                <input className="w-full mt-1 h-10 px-3 rounded-lg border border-slate-200" value={form.postcode || ''}
                  onChange={(e) => update({ postcode: e.target.value.toUpperCase() })} placeholder="e.g. SW11 1AA" data-testid="ob-input-postcode" /></div>
              <div><label className="text-sm font-medium">Maximum travel radius (miles)</label>
                <div className="flex flex-wrap gap-2 mt-1">
                  {WORK_RADIUS.map((r) => (
                    <button type="button" key={r} onClick={() => update({ work_radius_miles: r })}
                      className={`px-4 py-2 rounded-lg border text-sm ${form.work_radius_miles === r ? 'border-orange-500 bg-orange-50' : 'border-slate-200'}`}
                      data-testid={`ob-radius-${r}`}>{r} mi</button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeStep.key === 'rates' && (
            <div className="space-y-3" data-testid="step-rates">
              <label className="text-sm font-medium">How do you work?</label>
              <div className="flex flex-wrap gap-2">
                {WORK_PREFS.map((p) => {
                  const on = (form.preferred_work_type || []).includes(p.value);
                  return (
                    <button key={p.value} type="button"
                      onClick={() => {
                        const next = new Set(form.preferred_work_type || []);
                        if (p.value === 'both') {
                          update({ preferred_work_type: on ? [] : ['both'] });
                        } else {
                          next.delete('both');
                          on ? next.delete(p.value) : next.add(p.value);
                          update({ preferred_work_type: Array.from(next) });
                        }
                      }}
                      className={`px-4 py-2 rounded-lg border text-sm ${on ? 'border-orange-500 bg-orange-50' : 'border-slate-200'}`}
                      data-testid={`ob-pref-${p.value}`}>
                      {p.label}
                    </button>
                  );
                })}
              </div>
              {((form.preferred_work_type || []).includes('day_rate') || (form.preferred_work_type || []).includes('both')) && (
                <div><label className="text-sm font-medium">Day rate (£)</label>
                  <input type="number" min={0} className="w-full mt-1 h-10 px-3 rounded-lg border border-slate-200"
                    value={form.daily_rate ?? ''} onChange={(e) => update({ daily_rate: e.target.value })} data-testid="ob-input-daily-rate" /></div>
              )}
            </div>
          )}

          {activeStep.key === 'about' && (
            <div className="space-y-3" data-testid="step-about">
              <div><label className="text-sm font-medium">Short profile description</label>
                <textarea rows={4} className="w-full mt-1 px-3 py-2 rounded-lg border border-slate-200" value={form.bio || ''}
                  maxLength={500} onChange={(e) => update({ bio: e.target.value })}
                  placeholder="Say who you are, the work you do best and any specialisms." data-testid="ob-input-bio" />
                <p className="text-xs text-slate-400">{(form.bio || '').length}/500</p>
              </div>
              <div><label className="text-sm font-medium">Van / own transport?</label>
                <div className="flex gap-2 mt-1">
                  <button type="button" onClick={() => update({ own_transport: true })}
                    className={`px-4 py-2 rounded-lg border text-sm ${form.own_transport === true ? 'border-orange-500 bg-orange-50' : 'border-slate-200'}`}
                    data-testid="ob-transport-yes">Yes</button>
                  <button type="button" onClick={() => update({ own_transport: false })}
                    className={`px-4 py-2 rounded-lg border text-sm ${form.own_transport === false ? 'border-orange-500 bg-orange-50' : 'border-slate-200'}`}
                    data-testid="ob-transport-no">No</button>
                </div>
              </div>
              <div><label className="text-sm font-medium">Public liability insurance?</label>
                <div className="flex gap-2 mt-1">
                  <button type="button" onClick={() => update({ public_liability_insurance: true })}
                    className={`px-4 py-2 rounded-lg border text-sm ${form.public_liability_insurance === true ? 'border-orange-500 bg-orange-50' : 'border-slate-200'}`}
                    data-testid="ob-insurance-yes">Yes</button>
                  <button type="button" onClick={() => update({ public_liability_insurance: false })}
                    className={`px-4 py-2 rounded-lg border text-sm ${form.public_liability_insurance === false ? 'border-orange-500 bg-orange-50' : 'border-slate-200'}`}
                    data-testid="ob-insurance-no">No</button>
                </div>
                <p className="text-xs text-slate-500 mt-1">Answer honestly — this is not verified at this stage.</p>
              </div>
            </div>
          )}

          {activeStep.key === 'availability' && (
            <div className="space-y-3" data-testid="step-availability">
              <p className="text-sm text-slate-600">Confirm your status for each of the next 14 days. You can change these later from your dashboard.</p>
              <div className="flex flex-wrap gap-2 mb-2">
                <button type="button" onClick={() => setDateMap((m) => Object.fromEntries(Object.keys(m).map((d) => {
                  const day = new Date(d).getDay();
                  return [d, day === 0 || day === 6 ? 'unavailable' : 'available'];
                })))} className="text-xs px-2 py-1 rounded-full bg-slate-100" data-testid="ob-avail-quick-weekdays">Weekdays only</button>
                <button type="button" onClick={() => setDateMap((m) => Object.fromEntries(Object.keys(m).map((d) => [d, 'available'])))}
                  className="text-xs px-2 py-1 rounded-full bg-slate-100" data-testid="ob-avail-quick-everyday">Every day</button>
                <button type="button" onClick={() => setDateMap((m) => Object.fromEntries(Object.keys(m).map((d) => [d, 'unavailable'])))}
                  className="text-xs px-2 py-1 rounded-full bg-slate-100" data-testid="ob-avail-quick-none">Currently unavailable</button>
              </div>
              <div className="grid grid-cols-1 gap-1.5 max-h-96 overflow-y-auto pr-1">
                {Object.entries(dateMap).map(([d, status]) => (
                  <div key={d} className="flex items-center justify-between gap-2 border border-slate-100 rounded px-2 py-1.5">
                    <span className="text-xs font-medium text-slate-700 whitespace-nowrap">{new Date(d).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })}</span>
                    <select value={status || ''} onChange={(e) => setDateMap((m) => ({ ...m, [d]: e.target.value }))}
                      className={`text-xs h-8 px-2 rounded border border-slate-200 ${status ? (AVAIL_STATUSES.find((s) => s.value === status)?.cls || '') : ''}`}
                      data-testid={`ob-avail-select-${d}`}>
                      <option value="">— pick —</option>
                      {AVAIL_STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                    </select>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="flex items-center justify-between mt-6 gap-3">
            <button type="button" disabled={stepIdx === 0 || saving}
              onClick={() => setStepIdx((i) => Math.max(0, i - 1))}
              className="btn-secondary flex items-center gap-1 disabled:opacity-50" data-testid="ob-back">
              <ChevronLeft className="h-4 w-4" /> Back
            </button>
            {activeStep.key === 'availability' ? (
              <button type="button" onClick={submitAvailability} disabled={saving} className="btn-primary flex items-center gap-1" data-testid="ob-finish">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Confirm & finish
              </button>
            ) : (
              <button type="button" disabled={saving || !isCurrentStepValid} onClick={goNext}
                className="btn-primary flex items-center gap-1 disabled:opacity-50" data-testid="ob-next">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                Next <ChevronRight className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
